import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, CONTACT } from "../data";
import { track } from "../lib/track";

const SERVICES = [
  ["Recurring Home Cleaning", "A consistently clean home on your schedule — weekly, bi-weekly or monthly.", "Busy households that want a clean home without lifting a finger."],
  ["Deep Cleaning", "A top-to-bottom detailed clean that resets your entire home.", "First-time cleanings, seasonal refreshes and homes that need extra attention."],
  ["One-Time Cleaning", "Professional cleaning for a special occasion or whenever you need a hand.", "Hosting guests, special events, or catching up on life."],
  ["Move-In / Move-Out Cleaning", "A spotless handover — empty-home detailing, cabinets, appliances and floors.", "Tenants, landlords and homeowners in transition."],
  ["Airbnb / Short-Term Rental Cleaning", "Fast, reliable turnovers that keep your listing guest-ready.", "Hosts who want 5-star cleanliness without the stress."],
];

const BENEFITS = ["Your time back, every week", "A home that always feels ready for guests", "One trusted team, consistent results", "Easy booking and clear communication", "Flexible plans that fit your schedule", "Peace of mind — insured and professional"];

const FAQS = [
  ["Do I need to be home during the cleaning?", "No. Many of our customers provide access instructions and come home to a clean space. We communicate clearly before and after every visit."],
  ["What's included in a standard cleaning?", "Dusting of accessible surfaces, vacuuming and mopping, kitchen surfaces and appliance exteriors, full bathroom cleaning and trash removal. Add-ons like inside fridge or oven are available."],
  ["How is my price calculated?", "Your estimate is based on your home's size, condition and cleaning frequency. It's a starting estimate — we confirm final pricing based on your specific needs before any work begins."],
  ["Can I change or skip a visit?", "Yes. We ask for at least 48 hours notice to reschedule or skip a visit at no charge."],
  ["Are you insured?", "Yes — North Peak carries professional business insurance for your peace of mind."],
];

export default function Residential() {
  const { t } = useLang();
  return (
    <div data-testid="residential-page" className="bg-ink-950">
      <Seo title="Residential Cleaning Montreal" path="/residential" description="Professional home cleaning across Greater Montreal — recurring, deep, one-time, move-in/move-out and Airbnb cleaning. Come home to clean. Free estimates." keywords="House Cleaning Montreal, Residential Cleaning Montreal, Maid Service Montreal, Airbnb Cleaning Montreal, Move Out Cleaning Montreal" />
      <PageHero overline={t("Residential Cleaning")} title={t("Come home to clean.")}
        subtitle={t("Professional home cleaning for busy people who would rather spend their time living than cleaning.")}
        image={IMAGES.lobby3} />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 -mt-4">
        <Reveal>
          <Link to="/quote" data-testid="residential-hero-cta" onClick={() => track("cta_click", { id: "residential_hero" })}
            className="btn-pill group inline-flex bg-white text-ink-950 pl-7 pr-6 py-4 text-sm font-600 items-center gap-2 hover:shadow-xl hover:shadow-white/20">
            {t("Get My Home Cleaning Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <Reveal><h2 className="font-serif text-gradient text-3xl md:text-4xl mb-10" style={{ fontWeight: 500 }}>{t("Home cleaning services")}</h2></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map(([title, desc, who], i) => (
            <Reveal key={title} delay={(i % 3) * 0.08}>
              <div data-testid={`res-service-${i}`} className="glass card-hover rounded-3xl p-7 h-full flex flex-col">
                <h3 className="font-serif text-xl text-white" style={{ fontWeight: 500 }}>{t(title)}</h3>
                <p className="mt-2.5 text-white/55 text-sm leading-relaxed">{t(desc)}</p>
                <p className="mt-3 text-white/40 text-xs leading-relaxed flex-1"><span className="text-steel-light font-600">{t("For")}:</span> {t(who)}</p>
                <Link to="/quote" className="mt-5 inline-flex items-center gap-1.5 text-steel-light text-sm font-600 group">
                  {t("Get a quote")} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal><p className="overline text-steel-light mb-4">{t("Why North Peak")}</p></Reveal>
            <Reveal delay={0.08}><h2 className="font-serif text-gradient text-3xl md:text-4xl leading-[1.12] mb-7" style={{ fontWeight: 500 }}>{t("Relief, consistency and peace of mind.")}</h2></Reveal>
            <ul className="space-y-3">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/70">
                  <span className="h-5 w-5 rounded-full bg-steel/20 flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-steel-light" strokeWidth={2.5} /></span>
                  {t(b)}
                </li>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}><img src={IMAGES.heroInterior} alt={t("Clean, bright modern interior")} className="rounded-4xl w-full aspect-[4/3] object-cover opacity-80" loading="lazy" /></Reveal>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal><h2 className="font-serif text-gradient text-3xl md:text-4xl mb-10" style={{ fontWeight: 500 }}>{t("Questions, answered.")}</h2></Reveal>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`f-${i}`} className="glass rounded-2xl px-6 border-white/8">
              <AccordionTrigger className="font-serif text-lg text-white text-left hover:no-underline py-5" style={{ fontWeight: 500 }}>{t(q)}</AccordionTrigger>
              <AccordionContent className="text-white/55 leading-relaxed pb-5">{t(a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-12">
          <Link to="/quote" data-testid="residential-final-cta" onClick={() => track("cta_click", { id: "residential_final" })}
            className="btn-pill group inline-flex bg-white text-ink-950 pl-8 pr-6 py-4 text-sm font-600 items-center gap-2 hover:shadow-xl hover:shadow-white/20">
            {t("Get My Home Cleaning Quote")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="mt-4 text-white/40 text-sm">{t("Free estimates")} · {CONTACT.area} · <a href={CONTACT.phoneHref} onClick={() => track("phone_click", { where: "residential" })} className="text-steel-light">{CONTACT.phone}</a></p>
        </div>
      </section>
    </div>
  );
}
